import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Upload, Image as ImageIcon, Trash2, Plus, FileArchive, Link2, Sparkles } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const LibraryPhotoCard = ({ photo, isSelected, onToggle, onEdit }) => {
  const [error, setError] = useState(false);
  
  // Direct image URL - no fetch needed, browser loads it
  const imageUrl = `${API}/photos/${photo.id}/image?thumbnail=true`;

  return (
    <div
      className={`relative group cursor-pointer rounded-lg overflow-hidden border-4 transition-all ${
        isSelected
          ? 'border-blue-500 shadow-lg'
          : 'border-transparent hover:border-gray-300'
      }`}
      onClick={onToggle}
    >
      {error ? (
        <div className="w-full h-48 bg-red-100 flex items-center justify-center">
          <div className="text-red-600 text-xs">Failed to load</div>
        </div>
      ) : (
        <img
          src={imageUrl}
          alt={photo.filename}
          className="w-full h-48 object-cover bg-gray-200"
          loading="lazy"
          onError={() => {
            console.error('Image failed to load:', photo.id);
            setError(true);
          }}
        />
      )}
      {isSelected && (
        <div className="absolute top-2 right-2 bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center">
          ✓
        </div>
      )}
      {/* Edit button on hover */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onEdit(photo);
        }}
        className="absolute top-2 left-2 bg-white/90 text-gray-700 rounded-lg px-3 py-1 text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"
      >
        Edit
      </button>
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
        <p className="text-white text-xs truncate">{photo.caption || photo.filename}</p>
        {photo.location && <p className="text-white/80 text-xs truncate">{photo.location}</p>}
      </div>
    </div>
  );
};

const Library = () => {
  const navigate = useNavigate();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [showAddToAlbum, setShowAddToAlbum] = useState(false);
  const [albums, setAlbums] = useState([]);
  const [shoeboxes, setShoeboxes] = useState([]);
  const [photoUploadProgress, setPhotoUploadProgress] = useState(0);
  const [zipUploadProgress, setZipUploadProgress] = useState(0);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [zipUploading, setZipUploading] = useState(false);
  const [photoAbortController, setPhotoAbortController] = useState(null);
  const [zipAbortController, setZipAbortController] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingPhoto, setEditingPhoto] = useState(null);
  const [editForm, setEditForm] = useState({ caption: '', location: '', original_date: '' });
  const [recognitionStats, setRecognitionStats] = useState(null);

  useEffect(() => {
    fetchLibraryPhotos();
    fetchAlbums();
    fetchShoeboxes();
    fetchRecognitionStats();
  }, []);

  const fetchLibraryPhotos = async () => {
    try {
      const response = await axios.get(`${API}/library`);
      setPhotos(response.data);
    } catch (error) {
      toast.error('Failed to load library photos');
    } finally {
      setLoading(false);
    }
  };

  const fetchAlbums = async () => {
    try {
      const response = await axios.get(`${API}/albums`);
      setAlbums(response.data);
    } catch (error) {
      console.error('Failed to load albums');
    }
  };

  const fetchShoeboxes = async () => {
    try {
      const response = await axios.get(`${API}/shoeboxes`);
      setShoeboxes(response.data);
    } catch (error) {
      console.error('Failed to load shoeboxes');
    }
  };

  const fetchRecognitionStats = async () => {
    try {
      const response = await axios.get(`${API}/library/faces/stats`);
      setRecognitionStats(response.data);
    } catch (error) {
      console.error('Failed to load recognition stats');
    }
  };

  const handleDeleteShoebox = async (shoeboxId) => {
    if (!window.confirm('Delete this shoebox and all its photos from your library?')) {
      return;
    }

    try {
      await axios.delete(`${API}/shoeboxes/${shoeboxId}`);
      toast.success('Shoebox deleted');
      fetchShoeboxes();
      fetchLibraryPhotos();
    } catch (error) {
      toast.error('Failed to delete shoebox');
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const controller = new AbortController();
    setPhotoAbortController(controller);
    setPhotoUploading(true);
    setPhotoUploadProgress(0);
    
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    
    // Create shoebox name
    const shoeboxName = `Photos ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`;
    formData.append('shoebox_name', shoeboxName);

    try {
      const response = await axios.post(`${API}/library/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        signal: controller.signal,
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setPhotoUploadProgress(progress);
        }
      });
      toast.success(response.data.message);
      fetchLibraryPhotos();
      fetchShoeboxes();
    } catch (error) {
      if (error.name === 'CanceledError') {
        toast.info('Photo upload cancelled');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to upload photos');
      }
    } finally {
      setPhotoUploading(false);
      setPhotoUploadProgress(0);
      setPhotoAbortController(null);
      e.target.value = '';
    }
  };

  const cancelPhotoUpload = () => {
    if (photoAbortController) {
      photoAbortController.abort();
      setPhotoAbortController(null);
    }
  };

  const handleZipUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const controller = new AbortController();
    setZipAbortController(controller);
    setZipUploading(true);
    setZipUploadProgress(0);
    
    const formData = new FormData();
    formData.append('file', file);
    
    // Create shoebox name from ZIP filename
    const shoeboxName = `${file.name.replace('.zip', '')} - ${new Date().toLocaleDateString()}`;
    formData.append('shoebox_name', shoeboxName);

    try {
      const response = await axios.post(`${API}/library/upload-zip`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        signal: controller.signal,
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setZipUploadProgress(progress);
        }
      });
      toast.success(response.data.message);
      fetchLibraryPhotos();
      fetchShoeboxes();
    } catch (error) {
      if (error.name === 'CanceledError') {
        toast.info('ZIP upload cancelled');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to upload ZIP');
      }
    } finally {
      setZipUploading(false);
      setZipUploadProgress(0);
      setZipAbortController(null);
      e.target.value = '';
    }
  };

  const cancelZipUpload = () => {
    if (zipAbortController) {
      zipAbortController.abort();
      setZipAbortController(null);
    }
  };

  const togglePhotoSelection = (photoId) => {
    setSelectedPhotos(prev =>
      prev.includes(photoId)
        ? prev.filter(id => id !== photoId)
        : [...prev, photoId]
    );
  };

  const handleAddToAlbum = async (albumId) => {
    if (selectedPhotos.length === 0) {
      toast.error('Please select photos first');
      return;
    }

    try {
      const response = await axios.post(`${API}/albums/${albumId}/add-photos`, selectedPhotos);
      toast.success(response.data.message);
      setSelectedPhotos([]);
      setShowAddToAlbum(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add photos to album');
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedPhotos.length === 0) {
      toast.error('Please select photos first');
      return;
    }

    const confirmMessage = selectedPhotos.length === 1
      ? 'Delete this photo from your library?'
      : `Delete ${selectedPhotos.length} photos from your library?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      // Delete photos one by one
      for (const photoId of selectedPhotos) {
        await axios.delete(`${API}/photos/${photoId}`);
      }
      
      toast.success(`${selectedPhotos.length} photo(s) deleted successfully`);
      setSelectedPhotos([]);
      fetchLibraryPhotos();
      fetchShoeboxes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete photos');
    }
  };

  const handleEditPhoto = (photo) => {
    setEditingPhoto(photo);
    setEditForm({
      caption: photo.caption || '',
      location: photo.location || '',
      original_date: photo.original_date || ''
    });
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    try {
      await axios.put(`${API}/photos/${editingPhoto.id}`, editForm);
      toast.success('Photo updated successfully');
      setShowEditDialog(false);
      fetchLibraryPhotos();
    } catch (error) {
      toast.error('Failed to update photo');
    }
  };

  const handleRegenerateCaption = async () => {
    try {
      const response = await axios.post(`${API}/photos/${editingPhoto.id}/regenerate-caption`);
      setEditForm({ ...editForm, caption: response.data.caption });
      toast.success('Caption regenerated with AI!');
    } catch (error) {
      toast.error('Failed to regenerate caption');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-rose-50 to-blue-50">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-rose-50 to-blue-50">
      {/* Header */}
      <header className="bg-white/60 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Photo Library</h1>
                <p className="text-sm text-gray-600">{photos.length} photos</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* AI Library Manager Button - Always Visible */}
              <Button
                onClick={() => navigate('/library-manager')}
                className="bg-gradient-to-r from-purple-600 to-blue-600 text-white"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                AI Library Manager
              </Button>
              
              {selectedPhotos.length > 0 && (
                <>
                  <Button
                    onClick={() => setShowAddToAlbum(true)}
                    className="bg-gradient-to-r from-amber-600 to-rose-500 text-white"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add {selectedPhotos.length} to Album
                  </Button>
                  <Button
                    onClick={handleDeleteSelected}
                    variant="outline"
                    className="text-red-600 hover:bg-red-50 border-red-200"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete {selectedPhotos.length}
                  </Button>
                </>
              )}
              {/* Hidden file inputs */}
              <input
                id="library-upload"
                type="file"
                multiple
                accept="image/*"
                onChange={handleFileUpload}
                className="hidden"
              />
              <input
                id="zip-upload"
                type="file"
                accept=".zip"
                onChange={handleZipUpload}
                className="hidden"
              />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Face Recognition Training Section */}
        {recognitionStats && recognitionStats.total_faces > 0 && (
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-2xl p-6 border-2 border-purple-200 mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-xl font-bold text-purple-900 flex items-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  AI Face Recognition Training
                </h3>
                <p className="text-sm text-purple-700 mt-1">
                  Help AI identify people in your photos
                </p>
              </div>
              <Button
                onClick={() => navigate('/library-manager')}
                className="bg-gradient-to-r from-purple-600 to-blue-600 text-white"
              >
                Start Training
              </Button>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="bg-white/70 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-purple-600">{recognitionStats.total_faces}</p>
                <p className="text-xs text-gray-600">Total Faces</p>
              </div>
              <div className="bg-white/70 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-green-600">{recognitionStats.labeled_faces}</p>
                <p className="text-xs text-gray-600">Recognized</p>
              </div>
              <div className="bg-white/70 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-orange-600">{recognitionStats.unlabeled_faces}</p>
                <p className="text-xs text-gray-600">Unknown</p>
              </div>
              <div className="bg-white/70 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-blue-600">{recognitionStats.unique_people}</p>
                <p className="text-xs text-gray-600">People</p>
              </div>
              <div className="bg-white/70 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-indigo-600">{recognitionStats.recognition_percentage}%</p>
                <p className="text-xs text-gray-600">Complete</p>
              </div>
            </div>

            {recognitionStats.recognition_percentage < 50 && (
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  💡 <strong>Tip:</strong> Train the AI to recognize people for better photo organization and search!
                </p>
              </div>
            )}
          </div>
        )}

        {/* Photo Grid */}
        {photos.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p>Your library is empty. Import photos using the options above.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {photos.map((photo) => (
              <LibraryPhotoCard
                key={photo.id}
                photo={photo}
                isSelected={selectedPhotos.includes(photo.id)}
                onToggle={() => togglePhotoSelection(photo.id)}
                onEdit={handleEditPhoto}
              />
            ))}
          </div>
        )}
      </main>

      {/* Add to Album Dialog */}
      <Dialog open={showAddToAlbum} onOpenChange={setShowAddToAlbum}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add {selectedPhotos.length} Photos to Album</DialogTitle>
            <DialogDescription>Select an album to add the selected photos</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {albums.map((album) => (
              <button
                key={album.id}
                onClick={() => handleAddToAlbum(album.id)}
                className="w-full text-left p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
              >
                <h3 className="font-semibold text-gray-900">{album.title}</h3>
                <p className="text-sm text-gray-600">{album.photo_count} photos</p>
              </button>
            ))}
            {albums.length === 0 && (
              <p className="text-center text-gray-500 py-4">No albums yet. Create one first!</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Photo Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Photo Details</DialogTitle>
            <DialogDescription>Update caption, location, and date for this photo</DialogDescription>
          </DialogHeader>
          {editingPhoto && (
            <div className="space-y-4">
              <div className="mb-4">
                <img
                  src={`${API}/photos/${editingPhoto.id}/image?thumbnail=true`}
                  alt={editingPhoto.caption || editingPhoto.filename}
                  className="w-full h-48 object-cover rounded-lg"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="edit-caption">Caption</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleRegenerateCaption}
                    className="text-xs text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                  >
                    <Sparkles className="w-3 h-3 mr-1" />
                    Regenerate with AI
                  </Button>
                </div>
                <Textarea
                  id="edit-caption"
                  value={editForm.caption}
                  onChange={(e) => setEditForm({ ...editForm, caption: e.target.value })}
                  rows={3}
                  placeholder="Enter a caption for this photo..."
                />
                {editingPhoto.detected_people && editingPhoto.detected_people.length > 0 && (
                  <p className="text-xs text-gray-500">
                    💡 AI will include: {editingPhoto.detected_people.join(', ')}
                    {editingPhoto.original_date && ` • ${editingPhoto.original_date}`}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-location">Location</Label>
                <Input
                  id="edit-location"
                  value={editForm.location}
                  onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                  placeholder="e.g., Paris, France"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-date">Date</Label>
                <Input
                  id="edit-date"
                  type="date"
                  value={editForm.original_date || ''}
                  onChange={(e) => setEditForm({ ...editForm, original_date: e.target.value })}
                />
              </div>

              <div className="flex items-center space-x-2 pt-2 border-t">
                <input
                  type="checkbox"
                  id="is-unusable"
                  checked={editForm.is_unusable || false}
                  onChange={(e) => setEditForm({ ...editForm, is_unusable: e.target.checked })}
                  className="rounded border-gray-300"
                />
                <Label htmlFor="is-unusable" className="text-sm font-normal cursor-pointer">
                  Mark as unusable (blurry, duplicate, or poor quality)
                </Label>
              </div>
              <div className="flex gap-3 justify-end pt-4">
                <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveEdit} className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Library;
